'use client';

import { Loader2 } from 'lucide-react';
import { useRef } from 'react';
import { Textarea } from '@/lib/ui-kit/textarea';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { MAX_COMMENT_LENGTH } from './constants';
import { EmojiGifPicker } from './EmojiGifPicker';

interface CommentReplyFormProps {
  value: string;
  onValueChange: (value: string) => void;
  onSend: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isBlocked?: boolean;
  placeholder: string;
  sendLabel: string;
  cancelLabel: string;
  tooLongLabel: string;
  onSelectGif?: (id: string) => void;
}

export const CommentReplyForm = ({
  value,
  onValueChange,
  onSend,
  onCancel,
  isSubmitting,
  isBlocked = false,
  placeholder,
  sendLabel,
  cancelLabel,
  tooLongLabel,
  onSelectGif,
}: CommentReplyFormProps) => {
  const charCount = value.length;
  const isOverLimit = charCount > MAX_COMMENT_LENGTH;
  const showCounter = charCount >= MAX_COMMENT_LENGTH * 0.9;
  const sendDisabled = !value.trim() || isSubmitting || isBlocked || isOverLimit;

  // Last known caret position so picked emoji land at the cursor.
  const caretRef = useRef<number | null>(null);
  const insertEmoji = (emoji: string) => {
    const pos = caretRef.current ?? value.length;
    onValueChange(value.slice(0, pos) + emoji + value.slice(pos));
    caretRef.current = pos + emoji.length;
  };

  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div
          className={cn(
            'flex min-w-0 items-end gap-2 rounded-[10px] border px-4 py-3 transition-colors',
            isOverLimit
              ? 'border-destructive'
              : 'border-v2-text-secondary focus-within:border-v2-text-primary',
          )}
        >
          <Textarea
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onSelect={(e) => {
              caretRef.current = e.currentTarget.selectionStart;
            }}
            placeholder={placeholder}
            autoFocus
            disabled={isSubmitting}
            className={cn(
              'box-border max-h-[200px]! min-h-6! w-full min-w-0 flex-1 rounded-none! border-0! bg-transparent! p-0!',
              'text-[13px]! leading-[150%]! text-v2-text-primary! outline-none! ring-0! wrap-anywhere',
              'placeholder:text-v2-text-secondary! focus:outline-none! focus:ring-0!',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          {onSelectGif && (
            <EmojiGifPicker
              onSelectEmoji={insertEmoji}
              onSelectGif={onSelectGif}
              disabled={isSubmitting}
            />
          )}
        </div>
        {(isOverLimit || showCounter) && (
          <div className="flex items-center justify-between gap-2 text-[12px] leading-4">
            <span className={cn('text-destructive', !isOverLimit && 'invisible')}>
              {tooLongLabel}
            </span>
            <span className={isOverLimit ? 'text-destructive' : 'text-v2-text-secondary'}>
              {charCount}/{MAX_COMMENT_LENGTH}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          appearance="ghost"
          size="M"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {cancelLabel}
        </Button>
        <Button type="button" appearance="accent" size="M" onClick={onSend} disabled={sendDisabled}>
          {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : sendLabel}
        </Button>
      </div>
    </div>
  );
};
